import React, { ReactNode, RefObject } from "react";

import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import RefreshIcon from "@mui/icons-material/Refresh";
import TuneIcon from "@mui/icons-material/Tune";
import { CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import { Menu } from "components/popups";
import { MRT_Cell, MRT_Column, MRT_Row, MRT_TableInstance } from "material-react-table";
import { ReportPredictionTableRow } from "store/types/reports";

import { UpdateSetting } from "./PredictionTable";

interface Props {
  cell: MRT_Cell<ReportPredictionTableRow, unknown>;
  column: MRT_Column<ReportPredictionTableRow, unknown>;
  renderedCellValue: ReactNode;
  row: MRT_Row<ReportPredictionTableRow>;
  rowRef?: RefObject<HTMLTableRowElement | null>;
  staticColumnIndex?: number;
  staticRowIndex?: number;
  table: MRT_TableInstance<ReportPredictionTableRow>;

  updateSetting: UpdateSetting;
  onResetSetting: (data: { type: ReportPredictionTableRow["type"]; id: number; assetClassName: string }) => void;
  rowUpdatingId: number | null;
  cellUpdatingKey: string | null;
  menuRef: RefObject<HTMLButtonElement>;
  isDefaultSet: boolean;
}

export const FirstColumnCell: React.FC<Props> = ({
  renderedCellValue,
  row,

  updateSetting,
  onResetSetting,
  rowUpdatingId,
  menuRef,
  cellUpdatingKey,
  isDefaultSet
}) => {
  const rowData = row.original;
  const hasChildren = rowData.children && rowData.children.length > 0;
  const isExpanded = row.getIsExpanded();

  const handleChangeParamSetClick = () => {
    if (!menuRef.current || !row.id) return;
    updateSetting({
      anchorEl: menuRef.current,
      row: {
        id: rowData.id,
        type: rowData.type,
        name: rowData.name
      }
    });
  };

  return (
    <Stack
      sx={{
        flexDirection: "row",
        alignItems: "center",
        gap: "4px",
        justifyContent: "space-between",
        overflow: "hidden",
        "& .menu-trigger": {
          opacity: rowUpdatingId === rowData.id ? 1 : 0, // Show loading progress
          transition: "opacity 0.3s"
        },
        transition: "opacity 0.3s",
        "&:hover": {
          "& .menu-trigger": {
            opacity: 1
          }
        }
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{ flex: 1, minWidth: 0, ...(row.depth && { paddingLeft: row.depth * 24 + 4 + "px" }) }}
      >
        {/* Expand/Collapse Button */}
        <Stack direction="row" alignItems="center" sx={{ width: 28, justifyContent: "center" }}>
          {hasChildren && (
            <IconButton
              onClick={() => row.toggleExpanded()}
              sx={{
                width: 28,
                height: 28,
                padding: 0,
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)"
                }
              }}
            >
              {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
            </IconButton>
          )}
        </Stack>

        {/* Deal Name */}
        <Typography
          sx={{
            fontSize: "14px",
            lineHeight: "16px",
            fontWeight: hasChildren ? 500 : 400,
            color: "#071F35",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1
          }}
        >
          {renderedCellValue}
        </Typography>
      </Stack>
      {row.id && rowData.type && (
        <Menu
          {...(rowUpdatingId === rowData.id && { icon: <CircularProgress size={20} /> })}
          iconButtonProps={{
            className: "menu-trigger",
            ref: menuRef,
            disabled: Boolean(rowUpdatingId || cellUpdatingKey),
            ...(rowUpdatingId === rowData.id && { opacity: "1 !important" })
          }}
          keepTriggerEnabled
          options={[
            {
              title: "Change parameter set",
              icon: <TuneIcon />,
              onClick: handleChangeParamSetClick,
              disabled: isDefaultSet
            },
            {
              title: "Reset to default",
              icon: <RefreshIcon />,
              onClick: () => {
                if (!rowData.id) return;
                onResetSetting({ type: rowData.type, id: rowData.id, assetClassName: rowData.name });
              },
              disabled: isDefaultSet
            }
          ]}
        />
      )}
    </Stack>
  );
};
